/**
 * Workspace Commands
 *
 * Commands for creating workspace tabs (queries, notebooks, explorers, etc.)
 */

import {
  FileCode, Table, BookOpen, FolderInput, FileText, FileSearch,
  Settings, GitCompare, TableProperties, LayoutGrid, LayoutDashboard, Sparkles,
  Users, HardDrive, Activity, DatabaseBackup, Shield, Calendar,
  PanelBottomClose, FolderTree, ArrowRightLeft, Workflow, Map, Upload, Table2, Lock, History,
  RefreshCw,
} from 'lucide-react';
import { useDatabaseBlueprintSetupStore } from '../../database-blueprint/store/databaseBlueprintSetupStore';
import type { UserFacingEngineId } from '../../database-blueprint/types/databaseBlueprintTypes';
import type { CommandDefinition } from '../types/commandTypes';
import { useCommandPaletteStore } from '../store/commandPaletteStore';
import { useWorkspaceStore } from '../../workspace';
import { useContextSelectionStore } from '../../toolbar-context';
import { useDatabaseUIStore } from '../../database-objects/store/uiStore';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { useAiChatStore } from '../../ai-chat/store/aiChatStore';
import { useTableExplorerDialogStore } from '../../workspace/table-explorer';
import { openMigrateDatabaseTab } from '../../cross-engine-migrate/openMigrateTab';
import { openVqeFromActiveTab } from '../../workspace/visual-query-editor/workspace/openVqeFromActiveTab';
import { QUERY_TABLE_EVENT } from '../../workspace/query-editor/table-seed/useTableSeed';
import {
  hasActiveQueryTabRefreshTarget,
  refreshIntelliSenseForActiveQueryTab,
} from '../../intellisense/schemaRefresh/refreshTargets';
import { openDashboardTab } from '../../workspace/dashboards/utils/openDashboardTab';
import { openDataImportTab } from '../../workspace/data-import/openDataImportTab';
import { supportsDataImportEngine } from '../../workspace/data-import/mapping/targetEligibility';
import { openDeadlockAnalyzerTab } from '../../workspace/deadlock-analyzer/openDeadlockAnalyzerTab';
import { openQueryStoreTab, QUERY_STORE_MENU_LABEL } from '../../workspace/query-store/openQueryStoreTab';
import {
  supportsMssqlDbaEngineFilter,
  supportsQueryEditorEngineFilter,
  supportsTableExplorerEngineFilter,
} from '../../main-toolbar/model/toolbarEngineFilters';
import { importExecutionPlan } from '../../workspace/execution-plan/services/executionPlanService';
import { openSqlFile } from '../../workspace/query-editor/services/queryFilesApi';
import { hashText } from '../../workspace/query-editor/utils/queryFileUtils';
import { openNotebookFile } from '../../workspace/notebook/services/notebookFileApi';
import { deserializeFromIpynb } from '../../workspace/notebook/utils/ipynbDeserializer';
import { computeNotebookHash } from '../../workspace/notebook/utils/ipynbSerializer';
import { useScriptBrowserStore } from '../../script-browser/store/scriptBrowserStore';
import { isBrowserMode } from '../../browser-mode';
import { isBetaFeatureEnabled } from '../../../core/beta';
import { openSecurityManagerFromExplorerFolder } from '../../database-objects/services/securityExplorerOpenService';
import { isAnyJobSchedulerEngineConnected, hasJobSchedulerSupport, getJobSchedulerToolbarLabel } from '../../workspace/job-scheduler/engineConfig';
import { createEmptyView } from '../../workspace/spatial-explorer/store/spatialExplorerActions';
import { toast } from '@/components/ui/toaster';
import type { ResolvedContext } from '../../toolbar-context/types/contextTypes';
import { engineSupports, type DatabaseEngineId } from '@/core/engines';
import { isCommandAllowed } from '../../keybindings/dispatch/commandGate';
import { resolveActiveTarget } from '../../keybindings/dispatch/targetRegistry';
import { defaultKeybindings } from '../../keybindings/model/defaultKeybindings';
import type { KeybindingCommandId } from '../../keybindings/model/keybindingTypes';
import { safeConnectionAlias, safeConnectionServer } from '@/types/safeConnectionDisplay';

/** Close palette and run after a tick (prevents dialog conflicts with requestContext) */
function closeAndRun(
  fn: () => void | Promise<void>,
  gatedCommandId?: KeybindingCommandId,
) {
  useCommandPaletteStore.getState().close();
  setTimeout(() => {
    if (gatedCommandId) {
      const activeTabId = useWorkspaceStore.getState().activeTabId;
      const scope = defaultKeybindings[gatedCommandId].scope;
      const target = resolveActiveTarget(scope, activeTabId);
      if (!isCommandAllowed(gatedCommandId, 'palette', target)) return;
    }
    void fn();
  }, 0);
}

/** Build tab connectionInfo from resolved context */
function connInfo(ctx: ResolvedContext) {
  const validDb = useDatabaseUIStore.getState().getValidSelectedDatabase();
  const database = ctx.database ??
    (validDb?.connectionId === ctx.connectionId ? validDb.database : undefined);
  return {
    connectionId: ctx.connectionId,
    connectionName: ctx.connectionName,
    serverHost: ctx.serverHost,
    database,
  };
}

function fileName(filePath: string): string {
  return filePath.split(/[/\\]/).pop() || filePath;
}

function isAnyOracleConnected(): boolean {
  return useConnectionStore.getState().activeConnections
    .some((c) => c.status === 'connected' && c.connection?.engine === 'oracle');
}

function isAnyMssqlConnected(): boolean {
  return useConnectionStore.getState().activeConnections
    .some((c) => c.status === 'connected' && (c.connection?.engine === 'mssql' || c.connection?.engine === undefined));
}

function isAnyPerformanceDashboardEngineConnected(): boolean {
  return useConnectionStore.getState().activeConnections
    .some((c) => {
      if (c.status !== 'connected') return false;
      const engine = c.connection?.engine;
      return engine === 'oracle' || engine === 'mssql' || engine === 'pgsql' || engine === 'mysql' || engine === undefined;
    });
}

/** Engine filter for New Notebook's requestContext — excludes engines without notebook session support. */
export function supportsNotebooksEngineFilter(engine: string | undefined): boolean {
  return engineSupports((engine ?? 'mssql') as DatabaseEngineId, 'workspaceFeatures', 'supportsNotebooks');
}

/** Engine filter for Schema Overview's requestContext — excludes engines without Schema Overview support (kusto). */
export function supportsSchemaOverviewEngineFilter(engine: string | undefined): boolean {
  return engineSupports((engine ?? 'mssql') as DatabaseEngineId, 'workspaceFeatures', 'supportsSchemaOverview');
}

/** Engine filter for Spatial Explorer's requestContext — excludes engines without spatial types (kusto). */
export function supportsSpatialExplorerEngineFilter(engine: string | undefined): boolean {
  return engineSupports((engine ?? 'mssql') as DatabaseEngineId, 'spatial', 'supportsSpatialTypes');
}

/**
 * S1 Task 1.16 — a capability-gated entry stays available unless connections
 * ARE open and none of them can serve it. With nothing connected the entry
 * behaves exactly as before (it opens the Connections dialog), so the gate
 * only fires for, e.g., an Azure-Storage-only workspace.
 */
function everyConnectedConnectionLacks(filter: (engine: string | undefined) => boolean): boolean {
  const connected = useConnectionStore.getState().activeConnections
    .filter((c) => c.status === 'connected');
  return connected.length > 0 && !connected.some((c) => filter(c.connection?.engine));
}

function isQueryEditorReachable(): boolean {
  return !everyConnectedConnectionLacks(supportsQueryEditorEngineFilter);
}

function isTableExplorerReachable(): boolean {
  return !everyConnectedConnectionLacks(supportsTableExplorerEngineFilter);
}

function isAnySessionBrowserEngineConnected(): boolean {
  return useConnectionStore.getState().activeConnections
    .some((c) => {
      if (c.status !== 'connected') return false;
      const engine = c.connection?.engine;
      return engine === 'oracle' || engine === 'mssql' || engine === 'pgsql' || engine === 'mysql' || engine === undefined;
    });
}

export const workspaceCommands: CommandDefinition[] = [
  {
    id: 'workspace:new-query',
    label: 'New Query',
    category: 'workspace',
    icon: FileCode,
    needsContext: 'connection',
    isVisible: isQueryEditorReachable,
    execute: () => closeAndRun(async () => {
      const { getNextQueryNumber, addTab } = useWorkspaceStore.getState();
      const n = getNextQueryNumber();
      const ctx = await useContextSelectionStore.getState().requestContext(
        { requireConnection: true, requireDatabase: false, engineFilter: supportsQueryEditorEngineFilter },
        'New Query',
      );
      const eligible = ctx && supportsQueryEditorEngineFilter(ctx.engine) ? ctx : null;
      addTab({ title: `Query-${n}`, type: 'query', ...(eligible && { connectionInfo: connInfo(eligible) }) });
    }),
  },
  {
    id: 'workspace:new-query-with-ai',
    label: 'New Query with AI',
    category: 'workspace',
    icon: Sparkles,
    keywords: ['ai', 'copilot', 'assistant'],
    needsContext: 'connection',
    isVisible: isQueryEditorReachable,
    execute: () => closeAndRun(async () => {
      const { getNextQueryNumber, addTab } = useWorkspaceStore.getState();
      const n = getNextQueryNumber();
      const ctx = await useContextSelectionStore.getState().requestContext(
        { requireConnection: true, requireDatabase: false, engineFilter: supportsQueryEditorEngineFilter },
        'New Query with AI',
      );
      const eligible = ctx && supportsQueryEditorEngineFilter(ctx.engine) ? ctx : null;
      const tabId = addTab({
        title: `Query-${n}`, type: 'query', ...(eligible && { connectionInfo: connInfo(eligible) }),
      });
      queueMicrotask(() => useAiChatStore.getState().setSidebarOpen(tabId, true));
    }),
  },
  {
    id: 'workspace:table-explorer',
    label: 'Table Explorer',
    category: 'workspace',
    icon: Table,
    keywords: ['data', 'browse', 'rows'],
    needsContext: 'database',
    isVisible: isTableExplorerReachable,
    execute: () => closeAndRun(async () => {
      const ctx = await useContextSelectionStore.getState().requestContext(
        { requireConnection: true, requireDatabase: true, engineFilter: supportsTableExplorerEngineFilter },
        'Table Explorer',
      );
      if (!ctx?.database) return;
      const activeConns = useConnectionStore.getState().activeConnections;
      const ac = activeConns.find((c) => c.id === ctx.connectionId);
      const engineId = (ctx.engine || ac?.connection.engine || 'mssql') as DatabaseEngineId;
      // Re-validated after the async pick (the selection can change meanwhile).
      if (!supportsTableExplorerEngineFilter(engineId)) return;
      useTableExplorerDialogStore.getState().openDialog({
        connectionId: ctx.connectionId, connectionName: ctx.connectionName,
        serverHost: ctx.serverHost, database: ctx.database, engineId,
      });
    }),
  },
  {
    id: 'workspace:open-visual-query-editor',
    label: 'Toggle Visual view',
    category: 'workspace',
    icon: Workflow,
    keybindingId: 'global.openVisualQueryEditor',
    keywords: ['vqe', 'visual', 'visual query editor', 'sql', 'query', 'builder', 'diagram'],
    // No `needsContext` — the VQE self-handles the no-connection case via its
    // own readiness banner + starter canvas (Plan 7 §3.5 §3.6 S9). Mirrors
    // `openVqeFromActiveTab`'s other three entry points (toolbar / shortcut /
    // compact-promote); `source: 'palette'` feeds the unified
    // `feature:vqe_open` funnel.
    isVisible: () => isBetaFeatureEnabled('visual-query-editor'),
    execute: () => closeAndRun(() => {
      openVqeFromActiveTab(undefined, { source: 'palette', toggle: true });
    }),
  },
  {
    id: 'workspace:query-table',
    label: 'Query a table…',
    category: 'workspace',
    icon: Table2,
    keywords: ['table', 'select', 'top', 'rows', 'browse', 'seed', 'visual', 'vqe'],
    // Targets the ACTIVE query tab (the tab listens for the window event while
    // active), so the entry only shows when one is in front. Same beta gate as
    // the pencil: the seed's whole point is opening the compact visual editor.
    isVisible: () => {
      if (!isBetaFeatureEnabled('visual-query-editor')) return false;
      const { tabs, activeTabId } = useWorkspaceStore.getState();
      return tabs.find((t) => t.id === activeTabId)?.type === 'query';
    },
    execute: () => closeAndRun(() => {
      window.dispatchEvent(new CustomEvent(QUERY_TABLE_EVENT));
      window.electronAPI?.analytics
        ?.track('command-palette:query_table')
        .catch(() => { /* fire-and-forget */ });
    }),
  },
  {
    id: 'workspace:refresh-intellisense',
    label: 'Refresh IntelliSense',
    category: 'workspace',
    icon: RefreshCw,
    keybindingId: 'query.refreshIntellisense',
    keywords: ['intellisense', 'autocomplete', 'completion', 'schema', 'reload', 'refresh', 'cache'],
    // Targets the ACTIVE query tab's connection + database (same target the
    // Ctrl+Shift+R chord in MainShell resolves), so the entry only shows when
    // such a tab is in front.
    isVisible: () => hasActiveQueryTabRefreshTarget(),
    execute: () => closeAndRun(() => {
      refreshIntelliSenseForActiveQueryTab('palette');
      window.electronAPI?.analytics
        ?.track('command-palette:refresh_intellisense')
        .catch(() => { /* fire-and-forget */ });
    }),
  },
  {
    id: 'workspace:dashboards',
    label: 'Dashboards',
    category: 'workspace',
    icon: LayoutDashboard,
    keywords: ['dashboard', 'tiles', 'charts', 'kpi', 'monitor'],
    // No `needsContext` — dashboard tiles carry their own connection ids; the
    // tab self-handles the no-dashboard case with its zero state.
    isVisible: () => isBetaFeatureEnabled('dashboards'),
    execute: () => closeAndRun(() => {
      openDashboardTab();
      window.electronAPI?.analytics
        ?.track('command-palette:dashboards')
        .catch(() => { /* fire-and-forget */ });
    }),
  },
  {
    id: 'workspace:migrate-database',
    label: 'Migrate Database to Connection...',
    category: 'workspace',
    icon: ArrowRightLeft,
    keywords: ['migrate', 'cross-engine', 'copy', 'move', 'all tables', 'database'],
    isVisible: () => isBetaFeatureEnabled('cross-engine-migrate'),
    needsContext: 'database',
    execute: () => closeAndRun(async () => {
      const ctx = await useContextSelectionStore.getState().requestContext(
        { requireConnection: true, requireDatabase: true }, 'Migrate Database',
      );
      if (!ctx?.database) return;
      const ac = useConnectionStore.getState().activeConnections.find((c) => c.id === ctx.connectionId);
      const engine = (ctx.engine || ac?.connection.engine || 'mssql') as DatabaseEngineId;
      openMigrateDatabaseTab({
        connectionId: ctx.connectionId,
        connectionName: ctx.connectionName,
        serverHost: ctx.serverHost,
        database: ctx.database,
        engine,
      });
      window.electronAPI?.analytics
        ?.track('command-palette:migrate_database', { engine })
        .catch(() => { /* fire-and-forget */ });
    }),
  },
  {
    id: 'workspace:migrate-table',
    label: 'Migrate Table to Connection...',
    category: 'workspace',
    icon: ArrowRightLeft,
    keywords: ['migrate', 'cross-engine', 'copy', 'move', 'table'],
    isVisible: () => isBetaFeatureEnabled('cross-engine-migrate'),
    needsContext: 'database',
    execute: () => closeAndRun(async () => {
      const ctx = await useContextSelectionStore.getState().requestContext(
        { requireConnection: true, requireDatabase: true }, 'Migrate Table',
      );
      if (!ctx?.database) return;
      const ac = useConnectionStore.getState().activeConnections.find((c) => c.id === ctx.connectionId);
      const engineId = (ctx.engine || ac?.connection.engine || 'mssql') as DatabaseEngineId;
      useTableExplorerDialogStore.getState().openDialog({
        connectionId: ctx.connectionId, connectionName: ctx.connectionName,
        serverHost: ctx.serverHost, database: ctx.database, engineId,
        purpose: 'migrate-table',
      });
      window.electronAPI?.analytics
        ?.track('command-palette:migrate_table', { engine: engineId })
        .catch(() => { /* fire-and-forget */ });
    }),
  },
  {
    id: 'workspace:import-data',
    label: 'Import Data...',
    category: 'workspace',
    icon: Upload,
    keywords: ['import', 'csv', 'tsv', 'excel', 'xlsx', 'json', 'ndjson', 'load', 'flat file', 'data'],
    isVisible: () => isBetaFeatureEnabled('data-import'),
    needsContext: 'connection',
    execute: () => closeAndRun(async () => {
      const ctx = await useContextSelectionStore.getState().requestContext(
        { requireConnection: true, requireDatabase: false, engineFilter: supportsDataImportEngine }, 'Import Data',
      );
      if (!ctx) return;
      openDataImportTab({ connectionInfo: connInfo(ctx) });
      window.electronAPI?.analytics?.track('command-palette:import_data', {}).catch(() => {
        /* fire-and-forget */
      });
    }),
  },
  {
    id: 'workspace:new-notebook',
    label: 'New Notebook',
    category: 'workspace',
    icon: BookOpen,
    needsContext: 'connection',
    execute: () => closeAndRun(async () => {
      const { getNextNotebookNumber, addTab } = useWorkspaceStore.getState();
      const n = getNextNotebookNumber();
      const ctx = await useContextSelectionStore.getState().requestContext(
        { requireConnection: true, requireDatabase: false, engineFilter: supportsNotebooksEngineFilter }, 'New Notebook',
      );
      addTab({ title: `Notebook-${n}`, type: 'notebook', ...(ctx && { connectionInfo: connInfo(ctx) }) });
    }),
  },
  {
    id: 'workspace:open-notebook',
    label: 'Open Notebook',
    category: 'workspace',
    icon: FolderInput,
    keywords: ['ipynb', 'jupyter'],
    isEnabled: () => !isBrowserMode(),
    execute: () => closeAndRun(async () => {
      const result = await openNotebookFile({});
      if (!result.success) {
        if ('cancelled' in result && result.cancelled) return;
        toast.error({ title: 'Failed to open notebook', description: result.error });
        return;
      }
      try {
        const { content, warnings } = deserializeFromIpynb(result.content);
        const name = fileName(result.filePath);
        const hash = computeNotebookHash(content);
        useWorkspaceStore.getState().addTab({
          title: name, type: 'notebook',
          content: { ...content, filePath: result.filePath, lastSavedHash: hash },
        });
        const codeCells = content.cells.filter((c) => c.cellType === 'code').length;
        const mdCells = content.cells.filter((c) => c.cellType === 'markdown').length;
        useScriptBrowserStore.getState().recordScriptOpened(
          result.filePath, `${codeCells} code, ${mdCells} markdown`,
        );
        if (warnings.length > 0) {
          toast.warning({ title: 'Notebook imported with warnings', description: warnings.join('; ') });
        } else {
          toast.success({ title: 'Opened', description: result.filePath });
        }
      } catch (err) {
        toast.error({ title: 'Invalid .ipynb file', description: err instanceof Error ? err.message : 'Parse error' });
      }
    }),
  },
  {
    id: 'workspace:open-sql-file',
    label: 'Open SQL File',
    category: 'workspace',
    icon: FileText,
    keywords: ['sql', 'script', 'file'],
    isEnabled: () => !isBrowserMode(),
    execute: () => closeAndRun(async () => {
      const result = await openSqlFile({});
      if (!result.success) {
        if (!result.cancelled) toast.error({ title: 'Failed to open file', description: result.error });
        return;
      }
      const validDb = useDatabaseUIStore.getState().getValidSelectedDatabase();
      const query = result.query ?? '';
      let connectionInfo: { connectionId: string; connectionName: string; serverHost: string; database?: string } | undefined;
      if (validDb) {
        const { connections, activeConnections } = useConnectionStore.getState();
        const conn = connections.find((c) => c.id === validDb.connectionId);
        const active = activeConnections.find((c) => c.id === validDb.connectionId);
        // S1 Task 1.16 — the file still opens, but it is never attached to a
        // connection with no query language; the tab's own badge stays empty.
        if (supportsQueryEditorEngineFilter(conn?.engine ?? active?.connection.engine)) {
          connectionInfo = {
            connectionId: validDb.connectionId,
            connectionName: safeConnectionAlias(conn, 'Unnamed Connection'),
            serverHost: safeConnectionServer(active?.connection),
            database: validDb.database,
          };
        }
      }
      useWorkspaceStore.getState().addTab({
        title: fileName(result.filePath), type: 'query',
        ...(connectionInfo && { connectionInfo }),
        content: { query, filePath: result.filePath, fileLastSavedHash: hashText(query) },
      });
      useScriptBrowserStore.getState().recordScriptOpened(result.filePath, query.slice(0, 100));
      toast.success({ title: 'Opened', description: result.filePath });
    }),
  },
  {
    id: 'workspace:deadlock-analyzer',
    label: 'Deadlock Analyzer',
    category: 'workspace',
    icon: Lock,
    keywords: ['deadlock', 'xdl', 'xml_deadlock_report', 'lock', 'victim', 'mssql', 'sql server'],
    // No connection needed — the analyzer opens/pastes a report file.
    execute: () => closeAndRun(() => {
      openDeadlockAnalyzerTab({ entry: 'command-palette' });
    }),
  },
  {
    id: 'workspace:open-execution-plan',
    label: 'Open Execution Plan',
    category: 'workspace',
    icon: FileSearch,
    keywords: ['plan', 'performance', 'explain'],
    isEnabled: () => !isBrowserMode(),
    execute: () => closeAndRun(async () => {
      const result = await importExecutionPlan({});
      if (result.success) {
        useWorkspaceStore.getState().addTab({
          title: `Plan - ${fileName(result.filePath)}`, type: 'plan',
          content: { view: 'single', plan: result.artifact, filePath: result.filePath },
        });
      } else if (!result.cancelled) {
        toast.error({ title: 'Failed to open plan', description: result.error });
      }
    }),
  },
  {
    id: 'workspace:table-designer',
    label: 'Table Designer',
    category: 'workspace',
    icon: Settings,
    keywords: ['create table', 'design', 'ddl'],
    needsContext: 'database',
    execute: () => closeAndRun(async () => {
      const ctx = await useContextSelectionStore.getState().requestContext(
        { requireConnection: true, requireDatabase: true }, 'Table Designer',
      );
      if (!ctx?.database) return;
      useCommandPaletteStore.getState().openTableDesigner({
        connectionId: ctx.connectionId, database: ctx.database,
      });
    }),
  },
  {
    id: 'workspace:schema-compare',
    label: 'Schema Compare',
    category: 'workspace',
    icon: GitCompare,
    keywords: ['diff', 'schema', 'compare'],
    execute: () => {
      useCommandPaletteStore.getState().close();
      setTimeout(() => useCommandPaletteStore.getState().setPendingDialog('schema-compare'), 0);
    },
  },
  {
    id: 'workspace:data-compare',
    label: 'Data Compare',
    category: 'workspace',
    icon: TableProperties,
    keywords: ['diff', 'data', 'compare'],
    execute: () => {
      useCommandPaletteStore.getState().close();
      setTimeout(() => useCommandPaletteStore.getState().setPendingDialog('data-compare'), 0);
    },
  },
  {
    id: 'workspace:schema-overview',
    label: 'Schema Overview',
    category: 'workspace',
    icon: LayoutGrid,
    keywords: ['erd', 'diagram', 'overview'],
    needsContext: 'database',
    execute: () => closeAndRun(async () => {
      const ctx = await useContextSelectionStore.getState().requestContext(
        { requireConnection: true, requireDatabase: true, engineFilter: supportsSchemaOverviewEngineFilter }, 'Schema Overview',
      );
      if (!ctx?.database) return;
      useWorkspaceStore.getState().addTab({
        title: `Overview: ${ctx.database}`, type: 'schema-overview',
        connectionInfo: connInfo(ctx),
        content: { connectionId: ctx.connectionId, databaseName: ctx.database, filterMode: 'all', isLoading: true },
      });
    }),
  },
  {
    id: 'workspace:spatial-explorer',
    label: 'Spatial Explorer',
    category: 'workspace',
    icon: Map,
    keywords: ['map', 'geometry', 'geography', 'gis', 'postgis', 'spatial'],
    needsContext: 'database',
    execute: () => closeAndRun(async () => {
      window.electronAPI?.analytics?.track('command-palette:spatial_explorer').catch(() => {
        /* fire-and-forget */
      });
      const ctx = await useContextSelectionStore.getState().requestContext(
        { requireConnection: true, requireDatabase: true, engineFilter: supportsSpatialExplorerEngineFilter }, 'Spatial Explorer',
      );
      if (!ctx?.database) return;
      useWorkspaceStore.getState().addTab({
        title: `Map: ${ctx.database}`, type: 'spatial-explorer',
        connectionInfo: connInfo(ctx),
        content: { connectionId: ctx.connectionId, databaseName: ctx.database, view: createEmptyView() },
      });
    }),
  },
  {
    id: 'workspace:security-manager',
    label: 'Security Manager',
    category: 'workspace',
    icon: Shield,
    keywords: ['security', 'users', 'roles', 'permissions', 'logins', 'privileges'],
    needsContext: 'connection',
    execute: () => closeAndRun(async () => {
      const ctx = await useContextSelectionStore.getState().requestContext(
        { requireConnection: true, requireDatabase: false }, 'Security Manager',
      );
      if (!ctx) return;
      openSecurityManagerFromExplorerFolder({
        connectionId: ctx.connectionId,
        view: 'server',
      });
    }),
  },
  {
    id: 'workspace:session-browser',
    label: 'Session Browser',
    category: 'workspace',
    icon: Users,
    keywords: ['sessions', 'oracle', 'mssql', 'pgsql', 'postgresql', 'mysql', 'mariadb', 'active', 'spid', 'pid'],
    needsContext: 'connection',
    isVisible: isAnySessionBrowserEngineConnected,
    execute: () => closeAndRun(async () => {
      const ctx = await useContextSelectionStore.getState().requestContext(
        { requireConnection: true, requireDatabase: false }, 'Session Browser',
      );
      if (!ctx) return;
      useWorkspaceStore.getState().addTab({
        title: `Sessions: ${ctx.connectionName}`, type: 'session-browser',
        connectionInfo: connInfo(ctx),
        content: { filter: 'active', autoRefreshInterval: 0, isLoading: true },
      });
    }),
  },
  {
    id: 'workspace:tablespace-manager',
    label: 'Tablespace Manager',
    category: 'workspace',
    icon: HardDrive,
    keywords: ['tablespace', 'oracle', 'storage'],
    needsContext: 'connection',
    isVisible: isAnyOracleConnected,
    execute: () => closeAndRun(async () => {
      const ctx = await useContextSelectionStore.getState().requestContext(
        { requireConnection: true, requireDatabase: false }, 'Tablespace Manager',
      );
      if (!ctx) return;
      useWorkspaceStore.getState().addTab({
        title: `Tablespaces: ${ctx.connectionName}`, type: 'tablespace-manager',
        connectionInfo: connInfo(ctx),
        content: { isLoading: true },
      });
    }),
  },
  {
    id: 'workspace:query-store',
    label: QUERY_STORE_MENU_LABEL,
    category: 'workspace',
    icon: History,
    keywords: ['query store', 'regression', 'performance history', 'plan', 'mssql', 'sql server', 'slow query'],
    needsContext: 'database',
    isVisible: isAnyMssqlConnected,
    execute: () => closeAndRun(async () => {
      const ctx = await useContextSelectionStore.getState().requestContext(
        { requireConnection: true, requireDatabase: true, engineFilter: supportsMssqlDbaEngineFilter }, 'Query Store',
      );
      if (!ctx?.database) return;
      openQueryStoreTab({ entry: 'command-palette', connectionInfo: { ...connInfo(ctx), connectionId: ctx.connectionId, database: ctx.database } });
      window.electronAPI?.analytics?.track('command-palette:query_store').catch(() => { /* fire-and-forget */ });
    }),
  },
  {
    id: 'workspace:filegroup-manager',
    label: 'Filegroup Manager',
    category: 'workspace',
    icon: HardDrive,
    keywords: ['filegroup', 'mssql', 'storage', 'files', 'database files'],
    needsContext: 'connection',
    isVisible: isAnyMssqlConnected,
    execute: () => closeAndRun(async () => {
      const ctx = await useContextSelectionStore.getState().requestContext(
        { requireConnection: true, requireDatabase: false }, 'Filegroup Manager',
      );
      if (!ctx) return;
      useWorkspaceStore.getState().addTab({
        title: `Filegroups: ${ctx.connectionName}`, type: 'filegroup-manager',
        connectionInfo: connInfo(ctx),
        content: { isLoading: true },
      });
    }),
  },
  {
    id: 'workspace:instance-monitor',
    label: 'Instance Monitor',
    category: 'workspace',
    icon: Activity,
    keywords: ['monitor', 'oracle', 'mssql', 'pgsql', 'postgresql', 'mysql', 'mariadb', 'performance', 'dashboard', 'instance'],
    needsContext: 'connection',
    isVisible: isAnyPerformanceDashboardEngineConnected,
    execute: () => closeAndRun(async () => {
      const ctx = await useContextSelectionStore.getState().requestContext(
        { requireConnection: true, requireDatabase: false }, 'Instance Monitor',
      );
      if (!ctx) return;
      useWorkspaceStore.getState().addTab({
        title: `Instance: ${ctx.connectionName}`, type: 'instance-monitor',
        connectionInfo: connInfo(ctx),
        content: { activePanel: 'dashboard', autoRefreshInterval: 0, isLoading: true },
      });
    }),
  },
  {
    id: 'workspace:job-scheduler',
    label: 'Agent Jobs Manager',
    category: 'workspace',
    icon: Calendar,
    keywords: ['agent', 'jobs', 'scheduled', 'scheduler', 'sql server agent'],
    needsContext: 'connection',
    isVisible: () =>
      isAnyJobSchedulerEngineConnected() && isBetaFeatureEnabled('job-scheduler'),
    execute: () => closeAndRun(async () => {
      const ctx = await useContextSelectionStore.getState().requestContext(
        { requireConnection: true, requireDatabase: false, engineFilter: hasJobSchedulerSupport }, 'Agent Jobs Manager',
      );
      if (!ctx) return;
      const label = getJobSchedulerToolbarLabel(ctx.engine);
      useWorkspaceStore.getState().addTab({
        title: `${label}: ${ctx.connectionName}`, type: 'job-scheduler',
        connectionInfo: connInfo(ctx),
        content: { autoRefreshInterval: 0, isLoading: true },
      });
    }),
  },
  {
    id: 'workspace:data-pump',
    label: 'Data Pump',
    category: 'workspace',
    icon: DatabaseBackup,
    keywords: ['export', 'import', 'oracle', 'pump'],
    needsContext: 'connection',
    isVisible: isAnyOracleConnected,
    execute: () => closeAndRun(async () => {
      const ctx = await useContextSelectionStore.getState().requestContext(
        { requireConnection: true, requireDatabase: false }, 'Data Pump',
      );
      if (!ctx) return;
      useWorkspaceStore.getState().addTab({
        title: `Data Pump: ${ctx.connectionName}`, type: 'data-pump',
        connectionInfo: connInfo(ctx),
        content: { mode: 'export', wizardStep: 'prerequisites', isLoading: true },
      });
    }),
  },
  {
    id: 'workspace:database-blueprint',
    label: 'Database Blueprint to Folder',
    category: 'workspace',
    icon: FolderTree,
    keywords: ['blueprint', 'ddl', 'export', 'folder', 'sync', 'git'],
    needsContext: 'database',
    isVisible: () => isBetaFeatureEnabled('database-blueprint'),
    isEnabled: () => !isBrowserMode(),
    execute: () => closeAndRun(async () => {
      const ctx = await useContextSelectionStore.getState().requestContext(
        { requireConnection: true, requireDatabase: true }, 'Database Blueprint',
      );
      if (!ctx?.database) return;
      const activeConns = useConnectionStore.getState().activeConnections;
      const ac = activeConns.find((c) => c.id === ctx.connectionId);
      const engineId = (ctx.engine || ac?.connection.engine || 'mssql') as UserFacingEngineId;
      useDatabaseBlueprintSetupStore.getState().openSetupDialog({
        connectionId: ctx.connectionId,
        connectionName: ctx.connectionName,
        database: ctx.database,
        engine: engineId,
      });
      window.electronAPI?.analytics
        ?.track('command-palette:database_blueprint', { engine: engineId })
        .catch(() => {
          /* fire-and-forget */
        });
    }),
  },
  {
    id: 'workspace:toggle-results-pane',
    label: 'Toggle Results Pane',
    category: 'workspace',
    icon: PanelBottomClose,
    keybindingId: 'query.toggleResults',
    keywords: ['results', 'pane', 'collapse', 'hide', 'show', 'minimize', 'bottom'],
    execute: () => closeAndRun(() => {
      window.dispatchEvent(new CustomEvent('jam:query-editor:toggle-results-pane'));
    }, 'query.toggleResults'),
  },
];
